import { Copy, Plus, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { AdminInvitation, AdminVenue } from '../admin-types.js';
import { adminApi } from '../api.js';
import { Button, Field, Input, Notice } from './ui.js';

export function AdminVenuesPanel({
  venues,
  invitations,
  reload,
}: {
  venues: AdminVenue[];
  invitations: AdminInvitation[];
  reload: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [venueId, setVenueId] = useState(venues[0]?.id ?? '');
  const [created, setCreated] = useState<{ url: string; qrDataUrl: string } | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!venueId && venues[0]) setVenueId(venues[0].id);
  }, [venueId, venues]);

  const run = async (action: () => Promise<void>) => {
    setError('');
    try {
      await action();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'The action failed');
    }
  };
  const createVenue = () =>
    run(async () => {
      await adminApi('/api/admin/venues', {
        method: 'POST',
        body: JSON.stringify({
          name,
          city,
          ...(notificationEmail.trim() ? { notificationEmail } : {}),
          ...(contactName.trim() ? { contactName } : {}),
        }),
      });
      setName('');
      setCity('');
      setNotificationEmail('');
      setContactName('');
      await reload();
    });
  const createInvitation = () =>
    run(async () => {
      const value = await adminApi<{ url: string; qrDataUrl: string }>(
        `/api/admin/venues/${venueId}/invitations`,
        { method: 'POST', body: JSON.stringify({ language: 'en', expiresInDays: 30 }) },
      );
      setCreated(value);
      await reload();
    });
  const disable = (id: string) =>
    run(async () => {
      await adminApi(`/api/admin/invitations/${id}/disable`, { method: 'POST' });
      await reload();
    });

  return (
    <div className="admin-venues-workspace">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="admin-columns">
        <section className="admin-panel">
          <h2>
            <Plus size={19} /> Add venue
          </h2>
          <Field label="Name">
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="City">
            <Input value={city} onChange={(event) => setCity(event.target.value)} />
          </Field>
          <Field label="Lost-and-found email">
            <Input
              type="email"
              value={notificationEmail}
              onChange={(event) => setNotificationEmail(event.target.value)}
            />
          </Field>
          <Field label="Contact name, optional">
            <Input value={contactName} onChange={(event) => setContactName(event.target.value)} />
          </Field>
          <Button
            disabled={name.length < 2 || city.length < 2 || !notificationEmail.includes('@')}
            onClick={() => void createVenue()}
          >
            Add venue
          </Button>
        </section>
        <section className="admin-panel">
          <h2>
            <Plus size={19} /> Create invitation
          </h2>
          <Field label="Venue">
            <select
              className="input"
              value={venueId}
              onChange={(event) => setVenueId(event.target.value)}
            >
              {venues.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}, {venue.city}
                </option>
              ))}
            </select>
          </Field>
          <Button disabled={!venueId} onClick={() => void createInvitation()}>
            Create link and QR code
          </Button>
        </section>
        {created ? (
          <section className="created-invitation">
            <img src={created.qrDataUrl} alt="Invitation QR code" />
            <div>
              <h2>Invitation ready</h2>
              <p>{created.url}</p>
              <Button
                variant="secondary"
                onClick={() => void navigator.clipboard.writeText(created.url)}
              >
                <Copy size={17} /> Copy link
              </Button>
            </div>
          </section>
        ) : null}
      </div>

      <section className="admin-panel venue-directory">
        <h2>Venue contacts</h2>
        <div className="venue-edit-list">
          {venues.map((venue) => (
            <VenueEditor key={venue.id} venue={venue} reload={reload} />
          ))}
        </div>
      </section>

      <section className="admin-panel invitations-panel">
        <h2>Invitations</h2>
        {invitations.map((invitation) => (
          <div className="invitation-row" data-invitation-id={invitation.id} key={invitation.id}>
            <span>
              <strong>
                {venues.find((venue) => venue.id === invitation.venueId)?.name ??
                  invitation.venueId}
              </strong>
              <small>
                {invitation.exchangeCount} opens · {invitation.submissionCount} reports · expires{' '}
                {new Date(invitation.expiresAt).toLocaleDateString('en-GB')}
              </small>
            </span>
            {invitation.enabled ? (
              <Button variant="danger" onClick={() => void disable(invitation.id)}>
                Disable
              </Button>
            ) : (
              <em>Disabled</em>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}

function VenueEditor({ venue, reload }: { venue: AdminVenue; reload: () => Promise<void> }) {
  const [name, setName] = useState(venue.name);
  const [city, setCity] = useState(venue.city);
  const [email, setEmail] = useState(venue.notificationEmail ?? '');
  const [contactName, setContactName] = useState(venue.contactName ?? '');
  const [saved, setSaved] = useState(false);
  const save = async () => {
    await adminApi(`/api/admin/venues/${venue.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name,
        city,
        ...(email.trim() ? { notificationEmail: email } : {}),
        ...(contactName.trim() ? { contactName } : {}),
      }),
    });
    setSaved(true);
    await reload();
  };
  return (
    <article className="venue-editor">
      <div className="field-grid two">
        <Field label="Venue name">
          <Input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="City">
          <Input value={city} onChange={(event) => setCity(event.target.value)} />
        </Field>
        <Field label="Lost-and-found email">
          <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
        <Field label="Contact name">
          <Input value={contactName} onChange={(event) => setContactName(event.target.value)} />
        </Field>
      </div>
      <Button variant="secondary" disabled={!email.includes('@')} onClick={() => void save()}>
        <Save size={16} /> {saved ? 'Saved' : 'Save venue'}
      </Button>
    </article>
  );
}
